import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  FolderTree,
  ImagePlus,
  Images,
  Languages,
  Loader2,
  Package,
  Plus,
  Save,
  Trash2,
  WalletCards,
} from 'lucide-react';

import menuImportsApi from '../../api/menuImportsApi';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getUserEstablishmentId } from '../Users/userManagementHelpers';
import '../../styles/menu-import/menu-import.css';
import '../../styles/menu-import/menu-import-review.css';

const LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja'];
const LOCALE_NAMES = { de: 'Deutsch', zh: '中文', ru: 'Русский', nl: 'Nederlands', ja: '日本語' };
const MAX_PRODUCT_IMAGES = 30;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

const uid = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptyTranslations = (ingredients = false, locales = LOCALES) =>
  Object.fromEntries(
    locales.map((locale) => [
      locale,
      {
        name: '',
        description: '',
        ...(ingredients ? { ingredients: '' } : {}),
      },
    ]),
  );

const emptySizeTranslations = (locales = LOCALES) =>
  Object.fromEntries(
    locales.map((locale) => [locale, { name: '' }]),
  );

const createSize = (isDefault = false, locales = LOCALES) => ({
  translations: emptySizeTranslations(locales),
  price: 0,
  discount_price: null,
  is_default: isDefault,
});

function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  ariaLabel,
  placeholder = '—',
  className = '',
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selectedOption =
    options.find(
      (option) => String(option.value) === String(value ?? ''),
    ) || null;

  return (
    <div
      ref={rootRef}
      className={[
        'menu-import-custom-select',
        open ? 'is-open' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="menu-import-custom-select__trigger"
        onClick={() => {
          if (!disabled) {
            setOpen((current) => !current);
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <ChevronDown size={16} className={open ? 'is-rotated' : ''} />
      </button>

      {open ? (
        <div
          className="menu-import-custom-select__menu"
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const active =
              String(option.value) === String(value ?? '');

            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={active}
                className={active ? 'is-active' : ''}
                onClick={() => {
                  onChange?.(option.value);
                  setOpen(false);
                }}
              >
                <span>{option.label}</span>
                {active ? <Check size={15} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function MenuImportReview() {
  const { importId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const establishmentId = useMemo(
    () => getUserEstablishmentId(user),
    [user],
  );

  const [draft, setDraft] = useState(null);
  const [sourceLocale, setSourceLocale] = useState('fr');
  const [currency, setCurrency] = useState('MAD');
  const [menuName, setMenuName] = useState('');
  const [activeLocale, setActiveLocale] = useState('fr');
  const [selectedLocales, setSelectedLocales] = useState(LOCALES);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryImages, setCategoryImages] = useState({});
  const [productImages, setProductImages] = useState({});
  const categoryImagesRef = useRef({});
  const productImagesRef = useRef({});

  const categories = Array.isArray(draft?.categories)
    ? draft.categories
    : [];
  const products = Array.isArray(draft?.products)
    ? draft.products
    : [];

  const getLocaleDirection = (locale) =>
    String(locale || '')
      .toLowerCase()
      .startsWith('ar')
      ? 'rtl'
      : 'ltr';

  const formatNumber = (value) => {
    const number = Number(value || 0);

    try {
      return new Intl.NumberFormat(language || 'fr').format(number);
    } catch {
      return String(number);
    }
  };

  useEffect(() => {
    categoryImagesRef.current = categoryImages;
  }, [categoryImages]);

  useEffect(() => {
    productImagesRef.current = productImages;
  }, [productImages]);

  useEffect(() => {
    return () => {
      Object.values(categoryImagesRef.current).forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });

      Object.values(productImagesRef.current).forEach((items) => {
        (items || []).forEach((item) => {
          if (item?.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });
      });
    };
  }, []);

  const getLocaleLabel = (locale) =>
    LOCALE_NAMES[locale] || t(`aiImport.languages.${locale}`);

  const localeOptions = useMemo(
    () =>
      LOCALES.map((locale) => ({
        value: locale,
        label: LOCALE_NAMES[locale] || t(`aiImport.languages.${locale}`),
      })),
    [t],
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const response = await menuImportsApi.show(
          establishmentId,
          importId,
        );

        const record = response.data?.data || {};
        const extracted = record.extracted_data || {};

        const data = {
          ...extracted,
          categories: Array.isArray(extracted.categories)
            ? extracted.categories
            : [],
          products: Array.isArray(extracted.products)
            ? extracted.products
            : [],
          warnings: Array.isArray(extracted.warnings)
            ? extracted.warnings
            : [],
        };

        const detectedLocale =
          record.source_locale ||
          data.detected_source_locale ||
          'fr';

        const requestedLocales = Array.isArray(record.target_locales)
          ? record.target_locales.filter((locale) => LOCALES.includes(locale))
          : [];
        const availableLocales = requestedLocales.length ? requestedLocales : LOCALES;

        setSelectedLocales(availableLocales);
        setDraft(data);
        setSourceLocale(detectedLocale);
        setActiveLocale(availableLocales.includes(detectedLocale) ? detectedLocale : availableLocales[0]);
        setCurrency(record.currency || data.currency || 'MAD');
      } catch (requestError) {
        const message = getApiErrorMessage(requestError);

        showAlert({
          type: 'error',
          title: t('alerts.error'),
          message,
        });

        setDraft(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [establishmentId, importId, showAlert, t]);

  const updateCategory = (index, updater) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      const currentCategories = Array.isArray(current.categories)
        ? current.categories
        : [];

      return {
        ...current,
        categories: currentCategories.map((item, itemIndex) =>
          itemIndex === index ? updater(item) : item,
        ),
      };
    });
  };

  const updateProduct = (index, updater) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      const currentProducts = Array.isArray(current.products)
        ? current.products
        : [];

      return {
        ...current,
        products: currentProducts.map((item, itemIndex) =>
          itemIndex === index ? updater(item) : item,
        ),
      };
    });
  };

  const updateCategoryTranslation = (index, field, value) => {
    updateCategory(index, (item) => ({
      ...item,
      translations: {
        ...item.translations,
        [activeLocale]: {
          ...(item.translations?.[activeLocale] || {}),
          [field]: value,
        },
      },
    }));
  };

  const updateProductTranslation = (index, field, value) => {
    updateProduct(index, (item) => ({
      ...item,
      translations: {
        ...item.translations,
        [activeLocale]: {
          ...(item.translations?.[activeLocale] || {}),
          [field]: value,
        },
      },
    }));
  };

  const addCategory = () => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        categories: [
          ...(Array.isArray(current.categories) ? current.categories : []),
          {
            temp_key: uid('cat'),
            parent_temp_key: null,
            translations: emptyTranslations(false, selectedLocales),
          },
        ],
      };
    });
  };

  const deleteCategory = (index) => {
    const target = categories[index];

    if (!target) {
      return;
    }

    const removedProductKeys = products
      .filter((product) => product.category_temp_key === target.temp_key)
      .map((product) => product.temp_key);

    setCategoryImages((current) => {
      const next = { ...current };
      const item = next[target.temp_key];

      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }

      delete next[target.temp_key];
      return next;
    });

    if (removedProductKeys.length) {
      setProductImages((current) => {
        const next = { ...current };

        removedProductKeys.forEach((tempKey) => {
          (next[tempKey] || []).forEach((item) => {
            if (item?.previewUrl) {
              URL.revokeObjectURL(item.previewUrl);
            }
          });

          delete next[tempKey];
        });

        return next;
      });
    }

    setDraft((current) => {
      if (!current) {
        return current;
      }

      const currentCategories = Array.isArray(current.categories)
        ? current.categories
        : [];
      const currentProducts = Array.isArray(current.products)
        ? current.products
        : [];

      return {
        ...current,
        categories: currentCategories
          .filter((_, itemIndex) => itemIndex !== index)
          .map((category) =>
            category.parent_temp_key === target.temp_key
              ? { ...category, parent_temp_key: null }
              : category,
          ),
        products: currentProducts.filter(
          (product) => product.category_temp_key !== target.temp_key,
        ),
      };
    });
  };

  const addProduct = () => {
    if (!categories.length) {
      return;
    }

    setDraft((current) => {
      if (!current) {
        return current;
      }

      const currentCategories = Array.isArray(current.categories)
        ? current.categories
        : [];
      const currentProducts = Array.isArray(current.products)
        ? current.products
        : [];

      if (!currentCategories.length) {
        return current;
      }

      return {
        ...current,
        products: [
          ...currentProducts,
          {
            temp_key: uid('product'),
            category_temp_key: currentCategories[0].temp_key,
            translations: emptyTranslations(true, selectedLocales),
            pricing_type: 'fixed',
            base_price: 0,
            discount_price: null,
            sizes: [],
            supplements: [],
          },
        ],
      };
    });
  };

  const deleteProduct = (index) => {
    const target = products[index];

    if (target?.temp_key) {
      setProductImages((current) => {
        const next = { ...current };

        (next[target.temp_key] || []).forEach((item) => {
          if (item?.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });

        delete next[target.temp_key];
        return next;
      });
    }

    setDraft((current) => {
      if (!current) {
        return current;
      }

      const currentProducts = Array.isArray(current.products)
        ? current.products
        : [];

      return {
        ...current,
        products: currentProducts.filter(
          (_, itemIndex) => itemIndex !== index,
        ),
      };
    });
  };

  const handleCategoryImageChange = (categoryTempKey, event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!String(file.type || '').toLowerCase().startsWith('image/')) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('aiImport.review.invalidImage'),
      });
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('aiImport.review.imageTooLarge'),
      });
      return;
    }

    setCategoryImages((current) => {
      const existing = current[categoryTempKey];

      if (existing?.previewUrl) {
        URL.revokeObjectURL(existing.previewUrl);
      }

      return {
        ...current,
        [categoryTempKey]: {
          id: uid('category-image'),
          file,
          previewUrl: URL.createObjectURL(file),
        },
      };
    });
  };

  const removeCategoryImage = (categoryTempKey) => {
    setCategoryImages((current) => {
      const next = { ...current };
      const existing = next[categoryTempKey];

      if (existing?.previewUrl) {
        URL.revokeObjectURL(existing.previewUrl);
      }

      delete next[categoryTempKey];
      return next;
    });
  };

  const handleProductImagesChange = (productTempKey, event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (!selectedFiles.length) {
      return;
    }

    const nonImageFile = selectedFiles.find(
      (file) => !String(file.type || '').toLowerCase().startsWith('image/'),
    );

    if (nonImageFile) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('aiImport.review.invalidImage'),
      });
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_IMAGE_BYTES,
    );

    if (oversizedFile) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('aiImport.review.imageTooLarge'),
      });
      return;
    }

    const existingItems = productImages[productTempKey] || [];

    if (existingItems.length + selectedFiles.length > MAX_PRODUCT_IMAGES) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('aiImport.review.maxImages'),
      });
      return;
    }

    const newItems = selectedFiles.map((file) => ({
      id: uid('image'),
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setProductImages((current) => ({
      ...current,
      [productTempKey]: [
        ...(current[productTempKey] || []),
        ...newItems,
      ],
    }));
  };

  const removeProductImage = (productTempKey, imageId) => {
    setProductImages((current) => {
      const existingItems = current[productTempKey] || [];
      const target = existingItems.find((item) => item.id === imageId);

      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }

      return {
        ...current,
        [productTempKey]: existingItems.filter((item) => item.id !== imageId),
      };
    });
  };

  const getCategoryLabel = (category) =>
    category.translations?.[activeLocale]?.name ||
    category.translations?.[sourceLocale]?.name ||
    category.temp_key;

  const getProductLabel = (product) =>
    product.translations?.[activeLocale]?.name ||
    product.translations?.[sourceLocale]?.name ||
    t('aiImport.review.unnamedProduct');

  const getParentOptions = (category) => [
    {
      value: '',
      label: t('aiImport.review.rootCategory'),
    },
    ...categories
      .filter((item) => item.temp_key !== category.temp_key)
      .map((item) => ({
        value: item.temp_key,
        label: getCategoryLabel(item),
      })),
  ];

  const categoryOptions = categories.map((category) => ({
    value: category.temp_key,
    label: getCategoryLabel(category),
  }));

  const pricingOptions = [
    {
      value: 'fixed',
      label: t('aiImport.review.fixed'),
    },
    {
      value: 'sizes',
      label: t('aiImport.review.sizes'),
    },
  ];

  const handlePricingTypeChange = (productIndex, nextType) => {
    updateProduct(productIndex, (item) => ({
      ...item,
      pricing_type: nextType,
      base_price:
        nextType === 'fixed' ? (item.base_price ?? 0) : null,
      sizes:
        nextType === 'sizes' && !item.sizes?.length
          ? [createSize(true, selectedLocales)]
          : item.sizes || [],
    }));
  };

  const confirmImport = async () => {
    try {
      setSaving(true);

      await menuImportsApi.confirm(
        establishmentId,
        importId,
        {
          source_locale: sourceLocale,
          ...(menuName.trim() ? { menu_name: menuName.trim() } : {}),
          currency: currency || null,
          categories,
          products,
        },
        Object.fromEntries(
          Object.entries(categoryImages).map(([tempKey, item]) => [
            tempKey,
            item?.file || null,
          ]),
        ),
        Object.fromEntries(
          Object.entries(productImages).map(([tempKey, items]) => [
            tempKey,
            (items || []).map((item) => item.file),
          ]),
        ),
      );

      navigate(`/menu-imports/${importId}/success`, {
        replace: true,
      });
    } catch (requestError) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(requestError),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main
        className="menu-import-page menu-import-review-page"
        dir={direction}
      >
        <section className="menu-import-review-state">
          <Loader2 className="is-spinning" size={30} />
          <span>{t('common.loading')}</span>
        </section>
      </main>
    );
  }

  if (!draft) {
    return (
      <main
        className="menu-import-page menu-import-review-page"
        dir={direction}
      >
        <section className="menu-import-review-state">
          <button
            type="button"
            className="menu-import-review-back"
            onClick={() => navigate('/menu-import')}
          >
            <ArrowLeft size={18} />
            <span>{t('common.back')}</span>
          </button>
        </section>
      </main>
    );
  }

  return (
    <main
      className="menu-import-page menu-import-review-page"
      dir={direction}
    >
      <header className="menu-import-review-hero">
        <button
          type="button"
          className="menu-import-review-back"
          onClick={() => navigate('/menu-import')}
        >
          <ArrowLeft size={18} />
          <span>{t('common.back')}</span>
        </button>

        <div className="menu-import-review-hero__main">
          <div className="menu-import-review-hero__copy">
            <span className="menu-import-review-eyebrow">
              <Languages size={15} />
              {t('aiImport.steps.review')}
            </span>

            <h1>{t('aiImport.review.title')}</h1>
            <p>{t('aiImport.review.description')}</p>
          </div>

          <div className="menu-import-review-stats-v2">
            <article>
              <span>
                <FolderTree size={19} />
              </span>
              <div>
                <strong>{formatNumber(categories.length)}</strong>
                <small>{t('aiImport.review.categories')}</small>
              </div>
            </article>

            <article>
              <span>
                <Package size={19} />
              </span>
              <div>
                <strong>{formatNumber(products.length)}</strong>
                <small>{t('aiImport.review.products')}</small>
              </div>
            </article>
          </div>
        </div>
      </header>

      <div
        className="menu-import-steps menu-import-review-steps"
        aria-label={t('aiImport.steps.label')}
      >
        <span className="is-complete">1. {t('aiImport.steps.upload')}</span>
        <span className="is-complete">2. {t('aiImport.steps.analysis')}</span>
        <span className="is-active">3. {t('aiImport.steps.review')}</span>
        <span>4. {t('aiImport.steps.import')}</span>
      </div>

      {draft.warnings?.length > 0 ? (
        <section className="menu-import-warning-list">
          {draft.warnings.map((warning, index) => (
            <div key={`${warning.temp_key || 'warning'}-${index}`}>
              <AlertTriangle size={18} />
              <span>{warning.message}</span>
            </div>
          ))}
        </section>
      ) : null}

      <section className="menu-import-review-settings-v2">
        <div className="menu-import-review-settings-v2__head">
          <span>
            <Languages size={20} />
          </span>
          <div>
            <h2>{t('aiImport.review.sourceLanguage')}</h2>
            <p>{t('aiImport.review.description')}</p>
          </div>
        </div>

        <div className="menu-import-review-settings-v2__grid">
          <label className="menu-import-review-field">
            <span>Menu name (optional)</span>
            <div className="menu-import-review-input-wrap">
              <input
                type="text"
                value={menuName}
                onChange={(event) => setMenuName(event.target.value)}
                placeholder="Leave blank to use the default menu name"
                maxLength={150}
              />
            </div>
          </label>
          <label className="menu-import-review-field">
            <span>{t('aiImport.review.sourceLanguage')}</span>
            <CustomSelect
              value={sourceLocale}
              options={localeOptions}
              onChange={setSourceLocale}
              ariaLabel={t('aiImport.review.sourceLanguage')}
            />
          </label>

          <label className="menu-import-review-field">
            <span>{t('aiImport.review.currency')}</span>
            <div className="menu-import-review-input-wrap">
              <WalletCards size={17} />
              <input
                dir="ltr"
                value={currency}
                onChange={(event) =>
                  setCurrency(event.target.value.toUpperCase())
                }
                maxLength={10}
              />
            </div>
          </label>

          <div className="menu-import-review-locale-block">
            <span>{t('aiImport.languages.title')}</span>
            <div
              className="menu-import-locale-tabs menu-import-review-locale-tabs"
              aria-label={t('aiImport.languages.title')}
            >
              {selectedLocales.map((locale) => (
                <button
                  key={locale}
                  type="button"
                  className={activeLocale === locale ? 'is-active' : ''}
                  onClick={() => setActiveLocale(locale)}
                >
                  <strong>{locale.toUpperCase()}</strong>
                  <small>{getLocaleLabel(locale)}</small>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="menu-import-review-section">
        <div className="menu-import-review-section__head">
          <div>
            <span className="menu-import-review-section__icon">
              <FolderTree size={20} />
            </span>
            <div>
              <h2>{t('aiImport.review.categories')}</h2>
              <p>{t('aiImport.review.categoriesHint')}</p>
            </div>
          </div>

          <button
            type="button"
            className="menu-import-review-add-button"
            onClick={addCategory}
          >
            <Plus size={17} />
            <span>{t('aiImport.review.addCategory')}</span>
          </button>
        </div>

        <div className="menu-import-review-list">
          {categories.map((category, index) => (
            <article
              className="menu-import-review-entry-card"
              key={category.temp_key}
            >
              <div className="menu-import-review-category-grid">
                <label className="menu-import-review-field">
                  <span>{t('aiImport.review.categoryName')}</span>
                  <input
                    dir={getLocaleDirection(activeLocale)}
                    value={category.translations?.[activeLocale]?.name || ''}
                    placeholder={t('aiImport.review.categoryName')}
                    onChange={(event) =>
                      updateCategoryTranslation(
                        index,
                        'name',
                        event.target.value,
                      )
                    }
                  />
                </label>

                <label className="menu-import-review-field">
                  <span>{t('aiImport.review.parentCategory')}</span>
                  <CustomSelect
                    value={category.parent_temp_key || ''}
                    options={getParentOptions(category)}
                    onChange={(value) =>
                      updateCategory(index, (item) => ({
                        ...item,
                        parent_temp_key: value || null,
                      }))
                    }
                    ariaLabel={t('aiImport.review.parentCategory')}
                  />
                </label>

                <button
                  type="button"
                  className="menu-import-review-icon-button is-danger"
                  onClick={() => deleteCategory(index)}
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                >
                  <Trash2 size={17} />
                </button>
              </div>

              <label className="menu-import-review-field menu-import-review-field--full">
                <span>{t('aiImport.review.descriptionLabel')}</span>
                <textarea
                  dir={getLocaleDirection(activeLocale)}
                  value={
                    category.translations?.[activeLocale]?.description || ''
                  }
                  placeholder={t('aiImport.review.descriptionPlaceholder')}
                  onChange={(event) =>
                    updateCategoryTranslation(
                      index,
                      'description',
                      event.target.value,
                    )
                  }
                />
              </label>


              <div className="menu-import-review-image-editor">
                <div className="menu-import-review-image-editor__title">
                  <h3>{t('aiImport.review.categoryImage')}</h3>
                  <p>{t('aiImport.review.categoryImageHint')}</p>
                </div>

                <label className="menu-import-review-upload">
                  <ImagePlus size={28} />
                  <strong>{t('aiImport.review.addImage')}</strong>
                  <span>{t('aiImport.review.categoryImageHint')}</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(event) =>
                      handleCategoryImageChange(
                        category.temp_key,
                        event,
                      )
                    }
                  />
                </label>

                {categoryImages[category.temp_key] ? (
                  <div className="menu-import-review-image-grid menu-import-review-image-grid--single">
                    <figure className="menu-import-review-image-card">
                      <img
                        src={categoryImages[category.temp_key].previewUrl}
                        alt={getCategoryLabel(category)}
                      />

                      <button
                        type="button"
                        onClick={() =>
                          removeCategoryImage(category.temp_key)
                        }
                      >
                        <Trash2 size={15} />
                        {t('aiImport.review.removeImage')}
                      </button>
                    </figure>
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="menu-import-review-section">
        <div className="menu-import-review-section__head">
          <div>
            <span className="menu-import-review-section__icon">
              <Package size={20} />
            </span>
            <div>
              <h2>{t('aiImport.review.products')}</h2>
              <p>{t('aiImport.review.productsHint')}</p>
            </div>
          </div>

          <button
            type="button"
            className="menu-import-review-add-button"
            onClick={addProduct}
            disabled={!categories.length}
          >
            <Plus size={17} />
            <span>{t('aiImport.review.addProduct')}</span>
          </button>
        </div>

        <div className="menu-import-review-list">
          {products.map((product, index) => {
            const isOpen = expanded[product.temp_key] !== false;

            return (
              <article
                className={`menu-import-review-product-card ${
                  isOpen ? 'is-open' : ''
                }`}
                key={product.temp_key}
              >
                <div className="menu-import-review-product-card__head">
                  <button
                    type="button"
                    className="menu-import-review-icon-button menu-import-review-expand"
                    onClick={() =>
                      setExpanded((current) => ({
                        ...current,
                        [product.temp_key]: !isOpen,
                      }))
                    }
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>

                  <div>
                    <small>{t('aiImport.review.productName')}</small>
                    <strong>{getProductLabel(product)}</strong>
                  </div>

                  <button
                    type="button"
                    className="menu-import-review-icon-button is-danger"
                    onClick={() => deleteProduct(index)}
                    aria-label={t('common.delete')}
                    title={t('common.delete')}
                  >
                    <Trash2 size={17} />
                  </button>
                </div>

                {isOpen ? (
                  <div className="menu-import-review-product-card__body">
                    <div className="menu-import-review-product-grid">
                      <label className="menu-import-review-field">
                        <span>{t('aiImport.review.productName')}</span>
                        <input
                          dir={getLocaleDirection(activeLocale)}
                          value={
                            product.translations?.[activeLocale]?.name || ''
                          }
                          onChange={(event) =>
                            updateProductTranslation(
                              index,
                              'name',
                              event.target.value,
                            )
                          }
                        />
                      </label>

                      <label className="menu-import-review-field">
                        <span>{t('aiImport.review.category')}</span>
                        <CustomSelect
                          value={product.category_temp_key}
                          options={categoryOptions}
                          onChange={(value) =>
                            updateProduct(index, (item) => ({
                              ...item,
                              category_temp_key: value,
                            }))
                          }
                          ariaLabel={t('aiImport.review.category')}
                        />
                      </label>

                      <label className="menu-import-review-field">
                        <span>{t('aiImport.review.pricingType')}</span>
                        <CustomSelect
                          value={product.pricing_type}
                          options={pricingOptions}
                          onChange={(value) =>
                            handlePricingTypeChange(index, value)
                          }
                          ariaLabel={t('aiImport.review.pricingType')}
                        />
                      </label>

                      {product.pricing_type === 'fixed' ? (
                        <label className="menu-import-review-field">
                          <span>{t('aiImport.review.price')}</span>
                          <input
                            dir="ltr"
                            type="number"
                            min="0"
                            step="0.01"
                            value={product.base_price ?? ''}
                            onChange={(event) =>
                              updateProduct(index, (item) => ({
                                ...item,
                                base_price:
                                  event.target.value === ''
                                    ? null
                                    : Number(event.target.value),
                              }))
                            }
                          />
                        </label>
                      ) : null}
                    </div>

                    <div className="menu-import-review-text-grid">
                      <label className="menu-import-review-field">
                        <span>{t('aiImport.review.descriptionLabel')}</span>
                        <textarea
                          dir={getLocaleDirection(activeLocale)}
                          value={
                            product.translations?.[activeLocale]
                              ?.description || ''
                          }
                          placeholder={t(
                            'aiImport.review.descriptionPlaceholder',
                          )}
                          onChange={(event) =>
                            updateProductTranslation(
                              index,
                              'description',
                              event.target.value,
                            )
                          }
                        />
                      </label>

                      <label className="menu-import-review-field">
                        <span>{t('aiImport.review.ingredients')}</span>
                        <textarea
                          dir={getLocaleDirection(activeLocale)}
                          value={
                            product.translations?.[activeLocale]
                              ?.ingredients || ''
                          }
                          onChange={(event) =>
                            updateProductTranslation(
                              index,
                              'ingredients',
                              event.target.value,
                            )
                          }
                        />
                      </label>
                    </div>

                    <div className="menu-import-review-image-editor">
                      <div className="menu-import-review-image-editor__title">
                        <h3>{t('aiImport.review.productImages')}</h3>
                        <p>{t('aiImport.review.productImagesHint')}</p>
                      </div>

                      <label className="menu-import-review-upload">
                        <Images size={28} />
                        <strong>{t('aiImport.review.addImages')}</strong>
                        <span>{t('aiImport.review.productImagesHint')}</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          multiple
                          onChange={(event) =>
                            handleProductImagesChange(
                              product.temp_key,
                              event,
                            )
                          }
                        />
                      </label>

                      {(productImages[product.temp_key] || []).length > 0 ? (
                        <div className="menu-import-review-image-grid">
                          {(productImages[product.temp_key] || []).map(
                            (imageItem, imageIndex) => (
                              <figure
                                className="menu-import-review-image-card"
                                key={imageItem.id}
                              >
                                <img
                                  src={imageItem.previewUrl}
                                  alt={`${getProductLabel(product)} ${imageIndex + 1}`}
                                />

                                <button
                                  type="button"
                                  onClick={() =>
                                    removeProductImage(
                                      product.temp_key,
                                      imageItem.id,
                                    )
                                  }
                                >
                                  <Trash2 size={15} />
                                  {t('aiImport.review.removeImage')}
                                </button>
                              </figure>
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>

                    {product.pricing_type === 'sizes' ? (
                      <div className="menu-import-review-subeditor">
                        <div className="menu-import-review-subeditor__head">
                          <div>
                            <h3>{t('aiImport.review.sizes')}</h3>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              updateProduct(index, (item) => ({
                                ...item,
                                sizes: [
                                  ...(item.sizes || []),
                                  createSize(false, selectedLocales),
                                ],
                              }))
                            }
                          >
                            <Plus size={15} />
                            <span>{t('common.add')}</span>
                          </button>
                        </div>

                        <div className="menu-import-review-inline-list">
                          {(product.sizes || []).map((size, sizeIndex) => (
                            <div
                              className="menu-import-review-inline-row-v2"
                              key={`${product.temp_key}-size-${sizeIndex}`}
                            >
                              <label className="menu-import-review-field">
                                <span>{t('aiImport.review.sizeName')}</span>
                                <input
                                  dir={getLocaleDirection(activeLocale)}
                                  placeholder={t('aiImport.review.sizeName')}
                                  value={
                                    size.translations?.[activeLocale]?.name ||
                                    ''
                                  }
                                  onChange={(event) =>
                                    updateProduct(index, (item) => ({
                                      ...item,
                                      sizes: (item.sizes || []).map(
                                        (entry, entryIndex) =>
                                          entryIndex === sizeIndex
                                            ? {
                                                ...entry,
                                                translations: {
                                                  ...entry.translations,
                                                  [activeLocale]: {
                                                    ...(entry.translations?.[
                                                      activeLocale
                                                    ] || {}),
                                                    name: event.target.value,
                                                  },
                                                },
                                              }
                                            : entry,
                                      ),
                                    }))
                                  }
                                />
                              </label>

                              <label className="menu-import-review-field">
                                <span>{t('aiImport.review.price')}</span>
                                <input
                                  dir="ltr"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={size.price ?? ''}
                                  onChange={(event) =>
                                    updateProduct(index, (item) => ({
                                      ...item,
                                      sizes: (item.sizes || []).map(
                                        (entry, entryIndex) =>
                                          entryIndex === sizeIndex
                                            ? {
                                                ...entry,
                                                price:
                                                  event.target.value === ''
                                                    ? 0
                                                    : Number(
                                                        event.target.value,
                                                      ),
                                              }
                                            : entry,
                                      ),
                                    }))
                                  }
                                />
                              </label>

                              <button
                                type="button"
                                className="menu-import-review-icon-button is-danger"
                                onClick={() =>
                                  updateProduct(index, (item) => ({
                                    ...item,
                                    sizes: (item.sizes || []).filter(
                                      (_, entryIndex) =>
                                        entryIndex !== sizeIndex,
                                    ),
                                  }))
                                }
                                aria-label={t('common.delete')}
                                title={t('common.delete')}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    <div className="menu-import-review-subeditor">
                      <div className="menu-import-review-subeditor__head">
                        <div>
                          <h3>{t('aiImport.review.supplements')}</h3>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            updateProduct(index, (item) => ({
                              ...item,
                              supplements: [
                                ...(item.supplements || []),
                                {
                                  translations: emptyTranslations(false, selectedLocales),
                                  price: 0,
                                },
                              ],
                            }))
                          }
                        >
                          <Plus size={15} />
                          <span>{t('common.add')}</span>
                        </button>
                      </div>

                      <div className="menu-import-review-inline-list">
                        {(product.supplements || []).map(
                          (supplement, supplementIndex) => (
                            <div
                              className="menu-import-review-inline-row-v2"
                              key={`${product.temp_key}-supplement-${supplementIndex}`}
                            >
                              <label className="menu-import-review-field">
                                <span>
                                  {t('aiImport.review.supplementName')}
                                </span>
                                <input
                                  dir={getLocaleDirection(activeLocale)}
                                  placeholder={t(
                                    'aiImport.review.supplementName',
                                  )}
                                  value={
                                    supplement.translations?.[activeLocale]
                                      ?.name || ''
                                  }
                                  onChange={(event) =>
                                    updateProduct(index, (item) => ({
                                      ...item,
                                      supplements: (
                                        item.supplements || []
                                      ).map((entry, entryIndex) =>
                                        entryIndex === supplementIndex
                                          ? {
                                              ...entry,
                                              translations: {
                                                ...entry.translations,
                                                [activeLocale]: {
                                                  ...(entry.translations?.[
                                                    activeLocale
                                                  ] || {}),
                                                  name: event.target.value,
                                                },
                                              },
                                            }
                                          : entry,
                                      ),
                                    }))
                                  }
                                />
                              </label>

                              <label className="menu-import-review-field">
                                <span>{t('aiImport.review.price')}</span>
                                <input
                                  dir="ltr"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={supplement.price ?? ''}
                                  onChange={(event) =>
                                    updateProduct(index, (item) => ({
                                      ...item,
                                      supplements: (
                                        item.supplements || []
                                      ).map((entry, entryIndex) =>
                                        entryIndex === supplementIndex
                                          ? {
                                              ...entry,
                                              price:
                                                event.target.value === ''
                                                  ? 0
                                                  : Number(
                                                      event.target.value,
                                                    ),
                                            }
                                          : entry,
                                      ),
                                    }))
                                  }
                                />
                              </label>

                              <button
                                type="button"
                                className="menu-import-review-icon-button is-danger"
                                onClick={() =>
                                  updateProduct(index, (item) => ({
                                    ...item,
                                    supplements: (
                                      item.supplements || []
                                    ).filter(
                                      (_, entryIndex) =>
                                        entryIndex !== supplementIndex,
                                    ),
                                  }))
                                }
                                aria-label={t('common.delete')}
                                title={t('common.delete')}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      <div className="menu-import-actions menu-import-actions--sticky menu-import-review-footer">
        {direction === 'rtl' ? (
          <>
            <button
              type="button"
              className="menu-import-button menu-import-button--primary"
              disabled={saving || !categories.length}
              onClick={confirmImport}
            >
              {saving ? (
                <Loader2 className="is-spinning" size={18} />
              ) : (
                <Save size={18} />
              )}

              <span>
                {saving
                  ? t('aiImport.review.importing')
                  : t('aiImport.review.confirm')}
              </span>
            </button>

            <button
              type="button"
              className="menu-import-button"
              onClick={() => navigate('/menu-import')}
            >
              {t('common.cancel')}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="menu-import-button"
              onClick={() => navigate('/menu-import')}
            >
              {t('common.cancel')}
            </button>

            <button
              type="button"
              className="menu-import-button menu-import-button--primary"
              disabled={saving || !categories.length}
              onClick={confirmImport}
            >
              {saving ? (
                <Loader2 className="is-spinning" size={18} />
              ) : (
                <Save size={18} />
              )}

              <span>
                {saving
                  ? t('aiImport.review.importing')
                  : t('aiImport.review.confirm')}
              </span>
            </button>
          </>
        )}
      </div>
    </main>
  );
}
